// src/components/promotion/PromotionUpsertDialog.tsx
import * as React from "react";
import {
  Modal,
  Input,
  Select,
  Typography,
  Space,
  Divider,
  Switch,
  Table,
  Empty,
  Button,
  Spin,
  Collapse,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, PlusOutlined, HolderOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import type {
  PromotionDetail,
  PromotionCreatePayload,
  PromotionUpdatePayload,
  PromotionType,
  PromotionProduct,
  PromotionAddon,
  PromotionStatus,
  PromotionVolumePrice,
} from "src/api/promotion/promotionApi";
import { deletePromotionProductApi } from "src/api/promotion/promotionApi";
import ProductSelectModal, { SelectedProduct } from "./ProductSelectModal";
import CustomButton from "src/components/common/CustomButton";

type BaseProps = {
  open: boolean;
  onClose: () => void;
};

type CreateProps = BaseProps & {
  mode: "create";
  item?: never;
  onSubmit: (payload: PromotionCreatePayload, bannerImageFile?: File | null) => Promise<boolean>;
};

type EditProps = BaseProps & {
  mode: "edit";
  item: PromotionDetail;
  onSubmit: (payload: PromotionUpdatePayload, bannerImageFile?: File | null) => Promise<boolean>;
};

type Props = CreateProps | EditProps;

interface VariantRow {
  promotionProductId?: number;
  variantId: number;
  variantName: string;
  variantCode: string;
  optionPrice: number;
  consumerPrice: number;
  promotionPrice: number;
  maxQuantityPerProduct: number;
  maxQuantityPerOrder: number;
  isActive: boolean;
  displayOrder: number;
  productOrder?: number;
}

interface AddonRow {
  promotionProductAddonId?: number;
  mappingId: number;
  mappingName: string;
  addonPrice: number;
  consumerPrice: number;
  promotionPrice: number;
  isActive: boolean;
  displayOrder: number;
}

interface ProductRow {
  promotionProductId?: number; // 수정 모드에서만 존재 (단일 상품의 경우)
  productId: number;
  productName: string;
  promotionProductName?: string; // 프로모션에서 사용할 상품명 (사용자 수정 가능)
  productCode: string;
  productType: "SINGLE" | "OPTION";
  baseConsumerPrice: number; // 기본 상품가 (옵션가 계산용)
  // 단일상품용 필드
  consumerPrice: number;
  promotionPrice: number;
  maxQuantityPerProduct: number;
  maxQuantityPerOrder: number;
  isActive: boolean;
  displayOrder: number;
  // 옵션상품용 필드
  variants?: VariantRow[];
  // 추가상품 필드
  addonMappings?: AddonRow[];
}

// 숫자를 콤마 포함 문자열로 변환
const formatNumber = (value: number | string): string => {
  if (!value) return "0";
  const numValue = typeof value === "string" ? parseFloat(value) || 0 : value;
  return numValue.toLocaleString("ko-KR");
};

// 콤마 포함 문자열을 숫자로 변환
const parseNumber = (value: string): number => {
  const cleanValue = value.replace(/,/g, "");
  return parseFloat(cleanValue) || 0;
};

export default function PromotionUpsertDialog({ open, mode, item, onClose, onSubmit }: Props) {
  const [promotionName, setPromotionName] = React.useState("");
  const [promotionDescription, setPromotionDescription] = React.useState("");
  const [promotionType, setPromotionType] = React.useState<PromotionType>("EVENT");
  const [promotionStatus, setPromotionStatus] = React.useState<PromotionStatus>("DRAFT");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [displayOrder, setDisplayOrder] = React.useState(0);
  const [maxQuantity, setMaxQuantity] = React.useState(0);
  const [bannerImageUrl, setBannerImageUrl] = React.useState("");
  const [bannerImageFile, setBannerImageFile] = React.useState<File | null>(null);
  const [bannerImagePreview, setBannerImagePreview] = React.useState<string>("");
  const [bannerImageUploading, setBannerImageUploading] = React.useState(false);
  const bannerImageFileInputRef = React.useRef<HTMLInputElement>(null);

  const [products, setProducts] = React.useState<ProductRow[]>([]);
  const [productModalOpen, setProductModalOpen] = React.useState(false);
  const [promotionVolumePrices, setPromotionVolumePrices] = React.useState<PromotionVolumePrice[]>([]);

  const [submitting, setSubmitting] = React.useState(false);

  // 수정 모드일 때 초기값 세팅
  React.useEffect(() => {
    if (open && mode === "edit" && item) {
      setPromotionName(item.promotionName || "");
      setPromotionDescription(item.promotionDescription || "");
      setPromotionType(item.promotionType || "EVENT");
      setPromotionStatus(item.promotionStatus || "DRAFT");
      setStartDate(item.startDate ? item.startDate.slice(0, 16) : "");
      setEndDate(item.endDate ? item.endDate.slice(0, 16) : "");
      setDisplayOrder(item.displayOrder ?? 0);
      setMaxQuantity(item.maxQuantity ?? 0);
      setBannerImageUrl(item.bannerImageUrl || "");
      setBannerImageFile(null);
      setBannerImagePreview(item.bannerImageUrl || "");
      setBannerImageUploading(false);

      // promotionProducts를 상품 단위로 그룹화
      // 먼저 각 productId가 옵션 상품인지 판단
      const productTypeMap = new Map<number, "SINGLE" | "OPTION">();
      item.promotionProducts?.forEach((p) => {
        if (p.variantId != null) {
          productTypeMap.set(p.productId, "OPTION");
        } else if (!productTypeMap.has(p.productId)) {
          productTypeMap.set(p.productId, "SINGLE");
        }
      });

      const productMap = new Map<number, ProductRow>();
      item.promotionProducts?.forEach((p) => {
        const productType = productTypeMap.get(p.productId) || "SINGLE";

        if (!productMap.has(p.productId)) {
          // 새로운 상품 추가
          productMap.set(p.productId, {
            promotionProductId: productType === "SINGLE" ? p.promotionProductId : undefined,
            productId: p.productId,
            productName: p.productName,
            promotionProductName: p.promotionProductName || p.productName,
            productCode: p.productCode,
            productType: productType,
            baseConsumerPrice: p.consumerPrice,
            consumerPrice: productType === "OPTION" ? 0 : p.consumerPrice,
            promotionPrice: productType === "OPTION" ? 0 : p.promotionPrice,
            maxQuantityPerProduct: productType === "OPTION" ? 0 : p.maxQuantityPerProduct,
            maxQuantityPerOrder: productType === "OPTION" ? 0 : p.maxQuantityPerOrder,
            isActive: p.isActive ?? true,
            displayOrder: p.displayOrder ?? 0,
            variants: productType === "OPTION" ? [] : undefined,
            addonMappings: [],
          });
        }

        const product = productMap.get(p.productId)!;

        // variant가 있는 경우 variants 배열에 추가
        if (p.variantId != null) {
          product.variants = product.variants || [];
          product.variants.push({
            promotionProductId: p.promotionProductId,
            variantId: p.variantId,
            variantName: p.variantName || "",
            variantCode: "",
            optionPrice: 0,
            consumerPrice: p.consumerPrice,
            promotionPrice: p.promotionPrice,
            maxQuantityPerProduct: p.maxQuantityPerProduct,
            maxQuantityPerOrder: p.maxQuantityPerOrder,
            isActive: p.isActive ?? true,
            displayOrder: p.displayOrder ?? 0,
          });
        }
      });

      // promotionAddons를 별도로 처리
      item.promotionAddons?.forEach((addon) => {
        if (!productMap.has(addon.productId)) {
          // 상품이 없으면 스킵 (또는 기본 상품 정보 추가 필요)
          return;
        }
        const product = productMap.get(addon.productId)!;
        product.addonMappings = product.addonMappings || [];
        product.addonMappings.push({
          promotionProductAddonId: addon.promotionProductAddonId,
          mappingId: addon.addonMappingId,
          mappingName: addon.addonMappingName,
          addonPrice: addon.originalAddonPrice,
          consumerPrice: addon.originalAddonPrice,
          promotionPrice: addon.promotionAddonPrice,
          isActive: addon.isActive ?? true,
          displayOrder: addon.displayOrder ?? 0,
        });
      });

      setProducts(Array.from(productMap.values()));
      setPromotionVolumePrices(item.promotionVolumePrices || []);
    } else if (open && mode === "create") {
      setPromotionName("");
      setPromotionDescription("");
      setPromotionType("EVENT");
      setPromotionStatus("DRAFT");
      setStartDate("");
      setEndDate("");
      setDisplayOrder(0);
      setMaxQuantity(0);
      setBannerImageUrl("");
      setBannerImageFile(null);
      setBannerImagePreview("");
      setBannerImageUploading(false);
      setProducts([]);
      setPromotionVolumePrices([]);
    }
  }, [open, mode, item]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerImageFile(file);
      setBannerImageUrl("");
      const previewUrl = URL.createObjectURL(file);
      setBannerImagePreview(previewUrl);
    }
    e.currentTarget.value = "";
  };

  const handleBannerImageRemove = () => {
    if (bannerImagePreview && bannerImageFile) {
      URL.revokeObjectURL(bannerImagePreview);
    }
    setBannerImageFile(null);
    setBannerImageUrl("");
    setBannerImagePreview("");
  };

  const handleProductSelect = (selectedItems: SelectedProduct[]) => {
    const newRows: ProductRow[] = selectedItems.map((item) => {
      const existing = products.find((p) => p.productId === item.id);

      // addon mappings 처리
      const updatedAddons = item.addonMappings?.map((a) => {
        const existingAddon = existing?.addonMappings?.find((ea) => ea.mappingId === a.mappingId);
        if (existingAddon) {
          return existingAddon;
        } else {
          // 새로운 addon: 기본값으로 addonPrice를 consumerPrice에 설정
          return {
            mappingId: a.mappingId,
            mappingName: a.mappingName,
            addonPrice: a.addonPrice,
            consumerPrice: a.addonPrice,
            promotionPrice: 0,
            isActive: true,
            displayOrder: 0,
          };
        }
      });

      // 옵션 상품
      if (item.productType === "OPTION" && item.variants && item.variants.length > 0) {
        if (existing && existing.productType === "OPTION") {
          // 기존 옵션 상품: variants 정보 업데이트하면서 기존 입력값 유지
          const updatedVariants = item.variants.map((v) => {
            const existingVariant = existing.variants?.find((ev) => ev.variantId === v.variantId);
            if (existingVariant) {
              // 기존 variant가 있으면 입력값 유지
              return existingVariant;
            } else {
              // 새로운 variant: baseConsumerPrice + optionPrice를 정상가로 설정
              const basePrice = existing.baseConsumerPrice || item.baseConsumerPrice || 0;
              return {
                variantId: v.variantId,
                variantName: v.variantName,
                variantCode: v.variantCode,
                optionPrice: v.optionPrice,
                consumerPrice: basePrice + v.optionPrice,
                promotionPrice: 0,
                maxQuantityPerProduct: 0,
                maxQuantityPerOrder: 0,
                isActive: true,
                displayOrder: 0,
              };
            }
          });
          return {
            ...existing,
            productName: item.name,
            productCode: item.code,
            baseConsumerPrice: item.baseConsumerPrice || existing.baseConsumerPrice || 0,
            variants: updatedVariants,
            addonMappings: updatedAddons,
          };
        } else {
          // 새로운 옵션 상품
          return {
            productId: item.id,
            productName: item.name,
            promotionProductName: item.name,
            productCode: item.code,
            productType: "OPTION",
            baseConsumerPrice: item.baseConsumerPrice || 0,
            consumerPrice: 0,
            promotionPrice: 0,
            maxQuantityPerProduct: 0,
            maxQuantityPerOrder: 0,
            isActive: true,
            displayOrder: 0,
            variants: item.variants.map((v) => ({
              variantId: v.variantId,
              variantName: v.variantName,
              variantCode: v.variantCode,
              optionPrice: v.optionPrice,
              consumerPrice: (item.baseConsumerPrice || 0) + v.optionPrice,
              promotionPrice: 0,
              maxQuantityPerProduct: 0,
              maxQuantityPerOrder: 0,
              isActive: true,
              displayOrder: 0,
            })),
            addonMappings: updatedAddons,
          };
        }
      } else {
        // 단일 상품
        if (existing && existing.productType === "SINGLE") {
          // 기존 단일 상품: 입력값 유지
          return {
            ...existing,
            productName: item.name,
            productCode: item.code,
            baseConsumerPrice: item.baseConsumerPrice || existing.baseConsumerPrice || 0,
            addonMappings: updatedAddons,
          };
        } else {
          // 새로운 단일 상품
          return {
            productId: item.id,
            productName: item.name,
            promotionProductName: item.name,
            productCode: item.code,
            productType: "SINGLE",
            baseConsumerPrice: item.baseConsumerPrice || 0,
            consumerPrice: item.baseConsumerPrice || 0,
            promotionPrice: 0,
            maxQuantityPerProduct: 0,
            maxQuantityPerOrder: 0,
            isActive: true,
            displayOrder: 0,
            addonMappings: updatedAddons,
          };
        }
      }
    });
    setProducts(newRows);

    // volumePrices를 promotionVolumePrices로 변환 (productId 추가)
    const newPromotionVolumePrices: PromotionVolumePrice[] = [];
    selectedItems.forEach((item) => {
      if (item.volumePrices && item.volumePrices.length > 0) {
        // 기존 promotionVolumePrices에서 해당 상품의 volume prices가 있는지 확인
        const existingForProduct = promotionVolumePrices.filter(vp => vp.productId === item.id);
        if (existingForProduct.length > 0) {
          // 기존 값이 있으면 유지
          newPromotionVolumePrices.push(...existingForProduct);
        } else {
          // 새로운 상품이면 volumePrices에서 변환
          item.volumePrices.forEach((vp) => {
            newPromotionVolumePrices.push({
              productId: item.id,
              minQuantity: vp.minQuantity,
              discountType: vp.discountType,
              discountValue: vp.discountValue,
              isActive: vp.isActive,
            });
          });
        }
      }
    });
    // 기존에 있던 다른 상품의 volume prices도 유지
    promotionVolumePrices.forEach((vp) => {
      if (!selectedItems.find((item) => item.id === vp.productId)) {
        // 선택된 상품 중에 없으면 제거하지 않고 유지
        const productStillExists = newRows.find((row) => row.productId === vp.productId);
        if (productStillExists) {
          newPromotionVolumePrices.push(vp);
        }
      }
    });
    setPromotionVolumePrices(newPromotionVolumePrices);
  };

  const handleRemoveProduct = async (productId: number) => {
    const product = products.find((p) => p.productId === productId);
    if (!product) return;

    // 수정 모드일 때만 서버에 삭제 요청
    if (mode === "edit") {
      const promotionProductIdsToDelete: number[] = [];

      if (product.productType === "OPTION" && product.variants) {
        // 옵션 상품: 모든 variant의 promotionProductId 수집
        product.variants.forEach((v) => {
          if (v.promotionProductId) {
            promotionProductIdsToDelete.push(v.promotionProductId);
          }
        });
      } else if (product.productType === "SINGLE" && product.promotionProductId) {
        // 단일 상품: promotionProductId 수집
        promotionProductIdsToDelete.push(product.promotionProductId);
      }

      // 서버에서 삭제
      try {
        await Promise.all(promotionProductIdsToDelete.map((id) => deletePromotionProductApi(id)));
      } catch (error) {
        console.error("Failed to delete promotion product:", error);
        return;
      }
    }

    // 로컬 상태에서 제거
    setProducts((prev) => prev.filter((p) => p.productId !== productId));
    // 해당 상품의 promotionVolumePrices도 삭제
    setPromotionVolumePrices((prev) => prev.filter((vp) => vp.productId !== productId));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    setProducts((prev) => {
      const newProducts = [...prev];
      const [removed] = newProducts.splice(sourceIndex, 1);
      newProducts.splice(destinationIndex, 0, removed);
      return newProducts;
    });
  };

  const updateProductField = <K extends keyof ProductRow>(
    productId: number,
    field: K,
    value: ProductRow[K]
  ) => {
    setProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, [field]: value } : p))
    );
  };

  const updateVariantField = <K extends keyof VariantRow>(
    productId: number,
    variantId: number,
    field: K,
    value: VariantRow[K]
  ) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.productId === productId && p.variants) {
          return {
            ...p,
            variants: p.variants.map((v) =>
              v.variantId === variantId ? { ...v, [field]: value } : v
            ),
          };
        }
        return p;
      })
    );
  };

  const updateAddonField = <K extends keyof AddonRow>(
    productId: number,
    mappingId: number,
    field: K,
    value: AddonRow[K]
  ) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.productId === productId && p.addonMappings) {
          return {
            ...p,
            addonMappings: p.addonMappings.map((a) =>
              a.mappingId === mappingId ? { ...a, [field]: value } : a
            ),
          };
        }
        return p;
      })
    );
  };

  // 기본 상품가 업데이트 (옵션 상품의 경우 모든 variant의 정상가도 업데이트)
  const updateBaseConsumerPrice = (productId: number, newBasePrice: number) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.productId === productId) {
          if (p.productType === "OPTION" && p.variants) {
            // 옵션 상품: 모든 variant의 정상가를 새 기본가 + 옵션가로 업데이트
            return {
              ...p,
              baseConsumerPrice: newBasePrice,
              variants: p.variants.map((v) => ({
                ...v,
                consumerPrice: newBasePrice + v.optionPrice,
              })),
            };
          } else {
            // 단일 상품: baseConsumerPrice와 consumerPrice 모두 업데이트
            return {
              ...p,
              baseConsumerPrice: newBasePrice,
              consumerPrice: newBasePrice,
            };
          }
        }
        return p;
      })
    );
  };

  const handleSubmit = async () => {
    if (!promotionName.trim()) {
      alert("프로모션명을 입력해주세요.");
      return;
    }
    if (!startDate || !endDate) {
      alert("시작/종료 일시를 입력해주세요.");
      return;
    }
    if (products.length === 0) {
      alert("최소 1개 이상의 상품을 추가해주세요.");
      return;
    }

    // 상품을 평탄화하여 PromotionProduct와 PromotionAddon 배열로 변환
    const promotionProducts: PromotionProduct[] = [];
    const promotionAddons: PromotionAddon[] = [];

    products.forEach((p, index) => {
      // promotionProductName이 있으면 사용, 없으면 원래 productName 사용
      const finalProductName = p.promotionProductName?.trim() || p.productName;

      if (p.productType === "OPTION" && p.variants && p.variants.length > 0) {
        // 옵션 상품: 각 variant를 별도 아이템으로 추가
        p.variants.forEach((v) => {
          promotionProducts.push({
            productId: p.productId,
            variantId: v.variantId,
            promotionProductName: finalProductName,
            consumerPrice: v.consumerPrice,
            promotionPrice: v.promotionPrice,
            maxQuantityPerProduct: v.maxQuantityPerProduct,
            maxQuantityPerOrder: v.maxQuantityPerOrder,
            isActive: v.isActive,
            displayOrder: v.displayOrder,
            productOrder: index,
          });
        });
      } else {
        // 단일 상품: 상품 자체를 추가
        promotionProducts.push({
          productId: p.productId,
          variantId: null,
          promotionProductName: finalProductName,
          consumerPrice: p.consumerPrice,
          promotionPrice: p.promotionPrice,
          maxQuantityPerProduct: p.maxQuantityPerProduct,
          maxQuantityPerOrder: p.maxQuantityPerOrder,
          isActive: p.isActive,
          displayOrder: p.displayOrder,
          productOrder: index,
        });
      }

      // 추가상품은 별도 배열로 추가
      if (p.addonMappings && p.addonMappings.length > 0) {
        p.addonMappings.forEach((addon) => {
          promotionAddons.push({
            promotionProductAddonId: addon.promotionProductAddonId,
            productId: p.productId,
            addonMappingId: addon.mappingId,
            addonMappingName: addon.mappingName,
            originalAddonPrice: addon.addonPrice,
            promotionAddonPrice: addon.promotionPrice,
            isActive: addon.isActive,
            displayOrder: addon.displayOrder,
          });
        });
      }
    });

    setSubmitting(true);
    try {
      let success = false;
      if (mode === "create") {
        const payload: PromotionCreatePayload = {
          promotionName,
          promotionDescription,
          promotionType,
          promotionStatus,
          startDate,
          endDate,
          maxQuantity,
          promotionProducts,
          promotionAddons,
          promotionVolumePrices,
        };
        success = await (onSubmit as CreateProps["onSubmit"])(payload, bannerImageFile);
        console.log(payload);
      } else {
        const payload: PromotionUpdatePayload = {
          promotionName,
          promotionDescription,
          promotionType,
          promotionStatus,
          startDate,
          endDate,
          displayOrder,
          bannerImageUrl,
          maxQuantity,
          promotionProducts,
          promotionAddons,
          promotionVolumePrices,
        };
        success = await (onSubmit as EditProps["onSubmit"])(payload, bannerImageFile);
        console.log(payload);
      }

      if (success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedProductIds = products.map((p) => p.productId);
  const selectedProductItems: SelectedProduct[] = products.map((p) => ({
    id: p.productId,
    name: p.productName,
    code: p.productCode,
    productType: p.productType,
    baseConsumerPrice: p.baseConsumerPrice,
    baseSupplyPrice: 0,
    baseSalePrice: 0,
    variants:
      p.productType === "OPTION" && p.variants
        ? p.variants.map((v) => ({
            variantId: v.variantId,
            variantCode: v.variantCode,
            variantName: v.variantName,
            optionPrice: v.optionPrice,
          }))
        : undefined,
    addonMappings: p.addonMappings
      ? p.addonMappings.map((a) => ({
          mappingId: a.mappingId,
          mappingName: a.mappingName,
          addonPrice: a.addonPrice,
        }))
      : undefined,
  }));

  // 옵션 상품의 variants 테이블 columns
  const variantColumns: ColumnsType<VariantRow> = [
    { title: "옵션ID", dataIndex: "variantId", key: "variantId", width: 80 },
    { title: "옵션명", dataIndex: "variantName", key: "variantName", width: 150 },
    {
      title: "정상가",
      width: 100,
      render: (_, variant) => {
        const product = products.find((p) =>
          p.variants?.some((v) => v.variantId === variant.variantId)
        );
        return (
          <Input
            value={formatNumber(variant.consumerPrice)}
            onChange={(e) =>
              updateVariantField(
                product!.productId,
                variant.variantId,
                "consumerPrice",
                parseNumber(e.target.value)
              )
            }
            disabled
          />
        );
      },
    },
    {
      title: "프로모션가",
      width: 100,
      render: (_, variant) => {
        const product = products.find((p) =>
          p.variants?.some((v) => v.variantId === variant.variantId)
        );
        return (
          <Input
            value={formatNumber(variant.promotionPrice)}
            onChange={(e) =>
              updateVariantField(
                product!.productId,
                variant.variantId,
                "promotionPrice",
                parseNumber(e.target.value)
              )
            }
          />
        );
      },
    },
    {
      title: "상품당최대구매수",
      width: 100,
      render: (_, variant) => {
        const product = products.find((p) =>
          p.variants?.some((v) => v.variantId === variant.variantId)
        );
        return (
          <Input
            value={formatNumber(variant.maxQuantityPerProduct)}
            onChange={(e) =>
              updateVariantField(
                product!.productId,
                variant.variantId,
                "maxQuantityPerProduct",
                parseNumber(e.target.value)
              )
            }
          />
        );
      },
    },
    {
      title: "주문당최대구매수",
      width: 100,
      render: (_, variant) => {
        const product = products.find((p) =>
          p.variants?.some((v) => v.variantId === variant.variantId)
        );
        return (
          <Input
            value={formatNumber(variant.maxQuantityPerOrder)}
            onChange={(e) =>
              updateVariantField(
                product!.productId,
                variant.variantId,
                "maxQuantityPerOrder",
                parseNumber(e.target.value)
              )
            }
          />
        );
      },
    },
    {
      title: "활성화",
      width: 80,
      align: "center",
      render: (_, variant) => {
        const product = products.find((p) =>
          p.variants?.some((v) => v.variantId === variant.variantId)
        );
        return (
          <Switch
            checked={variant.isActive}
            onChange={(checked) =>
              updateVariantField(product!.productId, variant.variantId, "isActive", checked)
            }
          />
        );
      },
    },
    {
      title: "진열순서",
      width: 80,
      render: (_, variant) => {
        const product = products.find((p) =>
          p.variants?.some((v) => v.variantId === variant.variantId)
        );
        return (
          <Input
            value={formatNumber(variant.displayOrder)}
            onChange={(e) =>
              updateVariantField(
                product!.productId,
                variant.variantId,
                "displayOrder",
                parseNumber(e.target.value)
              )
            }
          />
        );
      },
    },
  ];

  // 추가상품 테이블 columns
  const addonColumns: ColumnsType<AddonRow> = [
    { title: "추가상품ID", dataIndex: "mappingId", key: "mappingId", width: 80 },
    { title: "추가상품명", dataIndex: "mappingName", key: "mappingName", width: 150 },
    {
      title: "정상가",
      width: 100,
      render: (_, addon) => {
        const product = products.find((p) =>
          p.addonMappings?.some((a) => a.mappingId === addon.mappingId)
        );
        return (
          <Input
            value={formatNumber(addon.consumerPrice)}
            onChange={(e) =>
              updateAddonField(
                product!.productId,
                addon.mappingId,
                "consumerPrice",
                parseNumber(e.target.value)
              )
            }
            disabled
          />
        );
      },
    },
    {
      title: "프로모션가",
      width: 100,
      render: (_, addon) => {
        const product = products.find((p) =>
          p.addonMappings?.some((a) => a.mappingId === addon.mappingId)
        );
        return (
          <Input
            value={formatNumber(addon.promotionPrice)}
            onChange={(e) =>
              updateAddonField(
                product!.productId,
                addon.mappingId,
                "promotionPrice",
                parseNumber(e.target.value)
              )
            }
          />
        );
      },
    },
    {
      title: "활성화",
      width: 80,
      align: "center",
      render: (_, addon) => {
        const product = products.find((p) =>
          p.addonMappings?.some((a) => a.mappingId === addon.mappingId)
        );
        return (
          <Switch
            checked={addon.isActive}
            onChange={(checked) =>
              updateAddonField(product!.productId, addon.mappingId, "isActive", checked)
            }
          />
        );
      },
    },
    {
      title: "진열순서",
      width: 80,
      render: (_, addon) => {
        const product = products.find((p) =>
          p.addonMappings?.some((a) => a.mappingId === addon.mappingId)
        );
        return (
          <Input
            value={formatNumber(addon.displayOrder)}
            onChange={(e) =>
              updateAddonField(
                product!.productId,
                addon.mappingId,
                "displayOrder",
                parseNumber(e.target.value)
              )
            }
          />
        );
      },
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={1100}
        centered
        title={
          <Typography.Text strong>
            {mode === "create" ? "프로모션 등록" : "프로모션 수정"}
          </Typography.Text>
        }
        footer={[
          <CustomButton key="cancel" variant="outlined" onClick={onClose} disabled={submitting}>
            취소
          </CustomButton>,
          <CustomButton
            key="submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "처리중..." : mode === "create" ? "등록" : "수정"}
          </CustomButton>,
        ]}
        styles={{
          body: {
            maxHeight: "calc(100vh - 250px)",
            overflowY: "auto",
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
          <Space size={16} wrap align="start">
            <div style={{ minWidth: 280, flex: 1 }}>
              <Typography.Text type="secondary">프로모션명 *</Typography.Text>
              <Input value={promotionName} onChange={(e) => setPromotionName(e.target.value)} />
            </div>
            <div style={{ minWidth: 200 }}>
              <Typography.Text type="secondary">프로모션 타입 *</Typography.Text>
              <Select<PromotionType>
                value={promotionType}
                onChange={(value) => setPromotionType(value)}
                options={[
                  { label: "이벤트", value: "EVENT" },
                  { label: "타임딜", value: "TIME_DEAL" },
                  { label: "플래시세일", value: "FLASH_SALE" },
                  { label: "시즌특가", value: "SEASONAL" },
                ]}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <Typography.Text type="secondary">최대 판매 수량</Typography.Text>
              <Input
                value={formatNumber(maxQuantity)}
                onChange={(e) => setMaxQuantity(parseNumber(e.target.value))}
              />
            </div>
          </Space>

          <div>
            <Typography.Text type="secondary">설명</Typography.Text>
            <Input.TextArea
              rows={3}
              value={promotionDescription}
              onChange={(e) => setPromotionDescription(e.target.value)}
            />
          </div>

          <Space size={16} wrap align="start">
            <div style={{ minWidth: 240 }}>
              <Typography.Text type="secondary">시작일시 *</Typography.Text>
              <Input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 240 }}>
              <Typography.Text type="secondary">종료일시 *</Typography.Text>
              <Input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {mode === "edit" ? (
              <div style={{ minWidth: 160 }}>
                <Typography.Text type="secondary">진열순서</Typography.Text>
                <Input
                  value={formatNumber(displayOrder)}
                  onChange={(e) => setDisplayOrder(parseNumber(e.target.value))}
                />
              </div>
            ) : null}
            <div style={{ minWidth: 200 }}>
              <Typography.Text type="secondary">프로모션 상태</Typography.Text>
              <Select<PromotionStatus>
                value={promotionStatus}
                onChange={(value) => setPromotionStatus(value)}
                options={[
                  { label: "임시저장", value: "DRAFT" },
                  { label: "활성화", value: "ACTIVE" },
                  { label: "취소", value: "CANCELED" },
                ]}
                style={{ width: "100%" }}
                disabled
              />
            </div>
          </Space>

          <Divider />

          <div>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              배너 이미지
            </Typography.Text>
            <div style={{ display: "flex", gap: 8 }}>
              {bannerImagePreview ? (
                <div
                  style={{
                    position: "relative",
                    width: 200,
                    height: 200,
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => !bannerImageUploading && bannerImageFileInputRef.current?.click()}
                >
                  <img
                    src={bannerImagePreview}
                    alt="배너 이미지"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {bannerImageUploading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <Spin />
                    </div>
                  )}
                  <input
                    ref={bannerImageFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBannerImageRemove();
                    }}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    }}
                  />
                </div>
              ) : (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 200,
                    height: 200,
                    border: "2px dashed #d9d9d9",
                    borderRadius: 8,
                    cursor: bannerImageUploading ? "not-allowed" : "pointer",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    if (!bannerImageUploading) e.currentTarget.style.borderColor = "#1890ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#d9d9d9";
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    disabled={bannerImageUploading}
                    onChange={handleFileChange}
                  />
                  {bannerImageUploading ? (
                    <Spin />
                  ) : (
                    <PlusOutlined style={{ fontSize: 32, color: "#999" }} />
                  )}
                </label>
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <Typography.Text strong>프로모션 상품 ({products.length}개)</Typography.Text>
              <CustomButton onClick={() => setProductModalOpen(true)}>상품 선택</CustomButton>
            </div>

            {products.length > 0 ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="products-list">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {products.map((product, index) => (
                        <Draggable
                          key={product.productId}
                          draggableId={String(product.productId)}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                marginBottom: 8,
                                ...provided.draggableProps.style,
                              }}
                            >
                              <Collapse
                                items={[
                                  {
                                    key: product.productId,
                                    label: (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 16,
                                        }}
                                      >
                                        <div
                                          {...provided.dragHandleProps}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: "move",
                                          }}
                                        >
                                          <HolderOutlined
                                            style={{
                                              fontSize: 16,
                                              color: "#999",
                                            }}
                                          />
                                        </div>
                                        <Typography.Text strong>
                                          {product.productName}
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                          {product.productCode}
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                          {product.productType === "SINGLE"
                                            ? "단일상품"
                                            : `옵션상품 (${product.variants?.length || 0}개)`}
                                        </Typography.Text>
                                      </div>
                                    ),
                                    extra: (
                                      <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await handleRemoveProduct(product.productId);
                                        }}
                                      />
                                    ),
                                    children: (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 16,
                                        }}
                                      >
                                        {/* 프로모션 상품명 수정 */}
                                        <div>
                                          <Typography.Text
                                            type="secondary"
                                            style={{ display: "block", marginBottom: 4 }}
                                          >
                                            프로모션 상품명 (비워두면 원래 상품명 사용)
                                          </Typography.Text>
                                          <Input
                                            value={product.promotionProductName || ""}
                                            onChange={(e) =>
                                              updateProductField(
                                                product.productId,
                                                "promotionProductName",
                                                e.target.value
                                              )
                                            }
                                            placeholder={product.productName}
                                          />
                                        </div>

                                        {/* 기본 상품가 수정 */}
                                        <div>
                                          <Typography.Text
                                            type="secondary"
                                            style={{ display: "block", marginBottom: 4 }}
                                          >
                                            기본 상품가{" "}
                                            {product.productType === "OPTION" &&
                                              "(모든 옵션에 적용)"}
                                          </Typography.Text>
                                          <Input
                                            value={formatNumber(product.baseConsumerPrice)}
                                            onChange={(e) =>
                                              updateBaseConsumerPrice(
                                                product.productId,
                                                parseNumber(e.target.value)
                                              )
                                            }
                                            disabled={product.productType === "OPTION"}
                                          />
                                        </div>

                                        {/* 단일 상품 설정 */}
                                        {product.productType === "SINGLE" ? (
                                          <div>
                                            <Typography.Text
                                              strong
                                              style={{ display: "block", marginBottom: 8 }}
                                            >
                                              상품 설정
                                            </Typography.Text>
                                            <div
                                              style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(2, 1fr)",
                                                gap: 16,
                                              }}
                                            >
                                              <div>
                                                <Typography.Text
                                                  type="secondary"
                                                  style={{ display: "block", marginBottom: 4 }}
                                                >
                                                  정상가
                                                </Typography.Text>
                                                <Input
                                                  value={formatNumber(product.consumerPrice)}
                                                  onChange={(e) =>
                                                    updateProductField(
                                                      product.productId,
                                                      "consumerPrice",
                                                      parseNumber(e.target.value)
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div>
                                                <Typography.Text
                                                  type="secondary"
                                                  style={{ display: "block", marginBottom: 4 }}
                                                >
                                                  프로모션가
                                                </Typography.Text>
                                                <Input
                                                  value={formatNumber(product.promotionPrice)}
                                                  onChange={(e) =>
                                                    updateProductField(
                                                      product.productId,
                                                      "promotionPrice",
                                                      parseNumber(e.target.value)
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div>
                                                <Typography.Text
                                                  type="secondary"
                                                  style={{ display: "block", marginBottom: 4 }}
                                                >
                                                  상품당 최대 수량
                                                </Typography.Text>
                                                <Input
                                                  value={formatNumber(
                                                    product.maxQuantityPerProduct
                                                  )}
                                                  onChange={(e) =>
                                                    updateProductField(
                                                      product.productId,
                                                      "maxQuantityPerProduct",
                                                      parseNumber(e.target.value)
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div>
                                                <Typography.Text
                                                  type="secondary"
                                                  style={{ display: "block", marginBottom: 4 }}
                                                >
                                                  주문당 최대 수량
                                                </Typography.Text>
                                                <Input
                                                  value={formatNumber(product.maxQuantityPerOrder)}
                                                  onChange={(e) =>
                                                    updateProductField(
                                                      product.productId,
                                                      "maxQuantityPerOrder",
                                                      parseNumber(e.target.value)
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div>
                                                <Typography.Text
                                                  type="secondary"
                                                  style={{ display: "block", marginBottom: 4 }}
                                                >
                                                  진열순서
                                                </Typography.Text>
                                                <Input
                                                  value={formatNumber(product.displayOrder)}
                                                  onChange={(e) =>
                                                    updateProductField(
                                                      product.productId,
                                                      "displayOrder",
                                                      parseNumber(e.target.value)
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 8,
                                                  paddingTop: 26,
                                                }}
                                              >
                                                <Switch
                                                  checked={product.isActive}
                                                  onChange={(checked) =>
                                                    updateProductField(
                                                      product.productId,
                                                      "isActive",
                                                      checked
                                                    )
                                                  }
                                                />
                                                <Typography.Text>활성화</Typography.Text>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          // 옵션 상품 설정
                                          <div>
                                            <Typography.Text
                                              strong
                                              style={{ display: "block", marginBottom: 8 }}
                                            >
                                              옵션 설정
                                            </Typography.Text>
                                            <Table
                                              columns={variantColumns}
                                              dataSource={product.variants || []}
                                              pagination={false}
                                              rowKey="variantId"
                                              size="small"
                                              scroll={{ x: 1000 }}
                                            />
                                          </div>
                                        )}

                                        {/* 추가상품 설정 */}
                                        {product.addonMappings &&
                                          product.addonMappings.length > 0 && (
                                            <div>
                                              <Typography.Text
                                                strong
                                                style={{ display: "block", marginBottom: 8 }}
                                              >
                                                추가상품 설정 ({product.addonMappings.length}개)
                                              </Typography.Text>
                                              <Table
                                                columns={addonColumns}
                                                dataSource={product.addonMappings}
                                                pagination={false}
                                                rowKey="mappingId"
                                                size="small"
                                                scroll={{ x: 1000 }}
                                              />
                                            </div>
                                          )}

                                        {/* 프로모션 수량별 할인 */}
                                        <div>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              marginBottom: 8,
                                            }}
                                          >
                                            <Typography.Text strong>
                                              수량별 할인
                                            </Typography.Text>
                                            <Button
                                              type="dashed"
                                              size="small"
                                              icon={<PlusOutlined />}
                                              onClick={() => {
                                                const currentVolumePrices = promotionVolumePrices.filter(
                                                  (vp) => vp.productId === product.productId
                                                );
                                                const lastMinQuantity =
                                                  currentVolumePrices.length > 0
                                                    ? currentVolumePrices[
                                                        currentVolumePrices.length - 1
                                                      ].minQuantity
                                                    : 0;
                                                setPromotionVolumePrices([
                                                  ...promotionVolumePrices,
                                                  {
                                                    productId: product.productId,
                                                    minQuantity: lastMinQuantity + 1,
                                                    discountType: "FIXED" as const,
                                                    discountValue: 0,
                                                    isActive: true,
                                                  },
                                                ]);
                                              }}
                                            >
                                              수량 할인 추가
                                            </Button>
                                          </div>

                                          {(() => {
                                            const currentVolumePrices = promotionVolumePrices.filter(
                                              (vp) => vp.productId === product.productId
                                            );
                                            return currentVolumePrices.length > 0 && (
                                              <>
                                                <Typography.Text
                                                  type="secondary"
                                                  style={{ display: "block", marginBottom: 12 }}
                                                >
                                                  특정 수량 이상 구매 시 프로모션가에서 추가 할인이
                                                  적용됩니다.
                                                </Typography.Text>

                                                <Table
                                                  dataSource={currentVolumePrices}
                                                  rowKey={(_, idx) => `volume-${product.productId}-${idx}`}
                                                  pagination={false}
                                                  size="small"
                                                  scroll={{ x: 1000 }}
                                                  columns={[
                                                    {
                                                      title: "최소 수량",
                                                      width: 120,
                                                      render: (_, vp, idx) => (
                                                        <Input
                                                          value={formatNumber(vp.minQuantity)}
                                                          onChange={(e) => {
                                                            const allVolumePrices = [...promotionVolumePrices];
                                                            const productVolumePrices = allVolumePrices.filter(
                                                              (v) => v.productId === product.productId
                                                            );
                                                            const newValue = parseNumber(e.target.value);
                                                            const prevMin =
                                                              idx > 0
                                                                ? productVolumePrices[idx - 1].minQuantity
                                                                : 0;

                                                            const globalIdx = allVolumePrices.findIndex(
                                                              (v) => v === productVolumePrices[idx]
                                                            );
                                                            allVolumePrices[globalIdx].minQuantity =
                                                              Math.max(prevMin + 1, newValue);
                                                            setPromotionVolumePrices(allVolumePrices);
                                                          }}
                                                        />
                                                      ),
                                                    },
                                                    {
                                                      title: "할인 금액/비율",
                                                      render: (_, vp, idx) => (
                                                        <div style={{ display: "flex", gap: 8 }}>
                                                          <Input
                                                            value={formatNumber(vp.discountValue)}
                                                            onChange={(e) => {
                                                              const allVolumePrices = [...promotionVolumePrices];
                                                              const productVolumePrices = allVolumePrices.filter(
                                                                (v) => v.productId === product.productId
                                                              );
                                                              const globalIdx = allVolumePrices.findIndex(
                                                                (v) => v === productVolumePrices[idx]
                                                              );
                                                              allVolumePrices[globalIdx].discountValue =
                                                                parseNumber(e.target.value);
                                                              setPromotionVolumePrices(allVolumePrices);
                                                            }}
                                                            style={{ flex: 1 }}
                                                          />
                                                          <Select
                                                            value={vp.discountType}
                                                            onChange={(val) => {
                                                              const allVolumePrices = [...promotionVolumePrices];
                                                              const productVolumePrices = allVolumePrices.filter(
                                                                (v) => v.productId === product.productId
                                                              );
                                                              const globalIdx = allVolumePrices.findIndex(
                                                                (v) => v === productVolumePrices[idx]
                                                              );
                                                              allVolumePrices[globalIdx].discountType = val;
                                                              setPromotionVolumePrices(allVolumePrices);
                                                            }}
                                                            style={{ width: 80 }}
                                                            options={[
                                                              { value: "FIXED", label: "원" },
                                                              { value: "PERCENTAGE", label: "%" },
                                                            ]}
                                                          />
                                                        </div>
                                                      ),
                                                    },
                                                    {
                                                      title: "활성",
                                                      width: 80,
                                                      align: "center",
                                                      render: (_, vp, idx) => (
                                                        <Switch
                                                          checked={vp.isActive}
                                                          onChange={(checked) => {
                                                            const allVolumePrices = [...promotionVolumePrices];
                                                            const productVolumePrices = allVolumePrices.filter(
                                                              (v) => v.productId === product.productId
                                                            );
                                                            const globalIdx = allVolumePrices.findIndex(
                                                              (v) => v === productVolumePrices[idx]
                                                            );
                                                            allVolumePrices[globalIdx].isActive = checked;
                                                            setPromotionVolumePrices(allVolumePrices);
                                                          }}
                                                          size="small"
                                                        />
                                                      ),
                                                    },
                                                    {
                                                      title: "삭제",
                                                      width: 80,
                                                      align: "center",
                                                      fixed: "right",
                                                      render: (_, vp) => (
                                                        <Button
                                                          type="text"
                                                          danger
                                                          size="small"
                                                          icon={<DeleteOutlined />}
                                                          onClick={() => {
                                                            setPromotionVolumePrices(
                                                              promotionVolumePrices.filter((v) => v !== vp)
                                                            );
                                                          }}
                                                        />
                                                      ),
                                                    },
                                                  ]}
                                                />
                                              </>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    ),
                                  },
                                ]}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <div style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: "16px 0" }}>
                <Empty description="상품을 선택해주세요." />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ProductSelectModal
        open={productModalOpen}
        selectedIds={selectedProductIds}
        selectedItems={selectedProductItems}
        onClose={() => setProductModalOpen(false)}
        onConfirm={handleProductSelect}
      />
    </>
  );
}
